import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon, 
  Highlighter,
  List,
  ListOrdered,
  Smile,
  Palette,
  Variable
} from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

export interface RichTextEditorHandle {
  insertAtCursor: (text: string) => void;
  focus: () => void;
}

const EMOJI_LIST = [
  "😀", "😃", "😄", "😁", "😊", "🙂", "😉", "😍", "🥰", "😘",
  "👍", "👏", "🙌", "🎉", "🎊", "✨", "⭐", "🌟", "💯", "🔥",
  "❤️", "💙", "💚", "💛", "🧡", "💜", "🖤", "🤍", "💖", "💗",
  "✅", "❌", "⚠️", "📌", "📍", "📅", "📆", "🗓️", "⏰", "🔔",
  "📧", "📩", "📨", "💬", "💭", "📞", "📱", "💻", "🖥️", "📊",
];

const HIGHLIGHT_COLORS = [
  { name: "Yellow", color: "#fef08a" },
  { name: "Green", color: "#bbf7d0" },
  { name: "Blue", color: "#bfdbfe" },
  { name: "Pink", color: "#fbcfe8" },
  { name: "Orange", color: "#fed7aa" },
  { name: "Purple", color: "#ddd6fe" },
];

const TEXT_COLORS = [
  { name: "Default", color: "inherit" },
  { name: "Red", color: "#ef4444" },
  { name: "Orange", color: "#f97316" },
  { name: "Green", color: "#22c55e" },
  { name: "Blue", color: "#3b82f6" },
  { name: "Purple", color: "#a855f7" },
  { name: "Pink", color: "#ec4899" },
];

export const EMAIL_MERGE_TOKENS = [
  { token: "{{contact_name}}", label: "Contact Name" },
  { token: "{{account_name}}", label: "Account Name" },
  { token: "{{client_name}}", label: "Client Name" },
  { token: "{{booking_details}}", label: "Booking Details" },
  { token: "{{date}}", label: "Event Date" },
  { token: "{{event_time}}", label: "Event Time" },
  { token: "{{location}}", label: "Location" },
  { token: "{{fee}}", label: "Fee" },
  { token: "{{booking_total}}", label: "Booking Total" },
  { token: "{{balance_due}}", label: "Balance Due" },
  { token: "{{invoice_number}}", label: "Invoice #" },
  { token: "{{invoice_url}}", label: "Invoice Link" },
  { token: "{{sender_name}}", label: "Sender Name" },
  { token: "{{talent}}", label: "Talent" },
  { token: "{{event_offering}}", label: "Event Offering" },
  { token: "{{media_assets_url}}", label: "Media Link" },
];

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
  showMergeTokens?: boolean;
  mergeTokens?: { token: string; label: string }[];
}

export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(function RichTextEditor({
  value,
  onChange,
  placeholder = "Write your message...",
  className,
  minHeight = "150px",
  showMergeTokens = false,
  mergeTokens,
}, ref) {
  const activeTokens = mergeTokens ?? EMAIL_MERGE_TOKENS;
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [highlightOpen, setHighlightOpen] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const [tokensOpen, setTokensOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Highlight.configure({ multicolor: true }),
      Underline,
      TextStyle,
      Color,
    ],
    content: value,
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm max-w-none focus:outline-none",
          "dark:prose-invert",
          "[&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1"
        ),
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  const hasBeenFocusedRef = useRef(false);
  const lastSelectionRef = useRef<{ from: number; to: number } | null>(null);

  useEffect(() => {
    if (!editor) return;
    const handleFocus = () => {
      hasBeenFocusedRef.current = true;
    };
    const handleSelectionUpdate = () => {
      if (editor.isFocused) {
        const { from, to } = editor.state.selection;
        lastSelectionRef.current = { from, to };
      }
    };
    editor.on('focus', handleFocus);
    editor.on('selectionUpdate', handleSelectionUpdate);
    return () => {
      editor.off('focus', handleFocus);
      editor.off('selectionUpdate', handleSelectionUpdate);
    };
  }, [editor]);

  useImperativeHandle(ref, () => ({
    insertAtCursor: (text: string) => {
      if (!editor) return;
      const chain = editor.chain();
      if (editor.isFocused) {
        chain.focus().insertContent(text).run();
        return;
      }
      if (hasBeenFocusedRef.current && lastSelectionRef.current) {
        const { from, to } = lastSelectionRef.current;
        chain.focus().setTextSelection({ from, to }).insertContent(text).run();
        return;
      }
      chain.focus('end').insertContent(text).run();
    },
    focus: () => {
      editor?.chain().focus().run();
    },
  }), [editor]);

  if (!editor) {
    return null;
  }

  const insertEmoji = (emoji: string) => {
    editor.chain().focus().insertContent(emoji).run();
    setEmojiOpen(false);
  };

  const insertToken = (token: string) => {
    editor.chain().focus().insertContent(token).run();
    setTokensOpen(false);
  };

  return (
    <div className={cn("border rounded-md bg-background", className)}>
      <div className="flex items-center gap-1 p-2 border-b bg-muted/30 flex-wrap">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn("h-8 w-8", editor.isActive("bold") && "bg-muted")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          data-testid="button-bold"
        >
          <Bold className="h-4 w-4" />
        </Button>
        
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn("h-8 w-8", editor.isActive("italic") && "bg-muted")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          data-testid="button-italic"
        >
          <Italic className="h-4 w-4" />
        </Button>
        
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn("h-8 w-8", editor.isActive("underline") && "bg-muted")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          data-testid="button-underline"
        >
          <UnderlineIcon className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1" />
        
        <Popover open={highlightOpen} onOpenChange={setHighlightOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn("h-8 w-8", editor.isActive("highlight") && "bg-muted")}
              data-testid="button-highlight"
            >
              <Highlighter className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="start">
            <div className="flex gap-1">
              {HIGHLIGHT_COLORS.map((item) => (
                <button
                  key={item.name}
                  type="button"
                  className="w-6 h-6 rounded border hover:scale-110 transition-transform"
                  style={{ backgroundColor: item.color }}
                  onClick={() => {
                    editor.chain().focus().toggleHighlight({ color: item.color }).run();
                    setHighlightOpen(false);
                  }}
                  title={item.name}
                />
              ))}
              <button
                type="button"
                className="w-6 h-6 rounded border bg-background hover:bg-muted flex items-center justify-center text-xs"
                onClick={() => {
                  editor.chain().focus().unsetHighlight().run();
                  setHighlightOpen(false);
                }}
                title="Remove highlight"
              >
                ✕
              </button>
            </div>
          </PopoverContent>
        </Popover>

        <Popover open={colorOpen} onOpenChange={setColorOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              data-testid="button-text-color"
            >
              <Palette className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="start">
            <div className="flex gap-1">
              {TEXT_COLORS.map((item) => (
                <button
                  key={item.name}
                  type="button"
                  className="w-6 h-6 rounded border hover:scale-110 transition-transform flex items-center justify-center"
                  style={{ color: item.color === "inherit" ? undefined : item.color }}
                  onClick={() => {
                    if (item.color === "inherit") {
                      editor.chain().focus().unsetColor().run();
                    } else {
                      editor.chain().focus().setColor(item.color).run();
                    }
                    setColorOpen(false);
                  }}
                  title={item.name}
                >
                  <span className="font-bold text-sm">A</span>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <div className="w-px h-6 bg-border mx-1" />

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn("h-8 w-8", editor.isActive("bulletList") && "bg-muted")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          data-testid="button-bullet-list"
        >
          <List className="h-4 w-4" />
        </Button>
        
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn("h-8 w-8", editor.isActive("orderedList") && "bg-muted")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          data-testid="button-ordered-list"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1" />

        <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              data-testid="button-emoji"
            >
              <Smile className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-2" align="start">
            <div className="grid grid-cols-10 gap-1">
              {EMOJI_LIST.map((emoji, index) => (
                <button
                  key={index}
                  type="button"
                  className="w-6 h-6 flex items-center justify-center hover:bg-muted rounded text-base"
                  onClick={() => insertEmoji(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {showMergeTokens && (
          <>
            <div className="w-px h-6 bg-border mx-1" />
            <Popover open={tokensOpen} onOpenChange={setTokensOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-1 px-2"
                  data-testid="button-merge-tokens"
                >
                  <Variable className="h-4 w-4" />
                  <span className="text-xs">Variables</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-3" align="start">
                <p className="text-xs text-muted-foreground mb-2">Click to insert at cursor:</p>
                <div className="flex flex-wrap gap-1.5">
                  {activeTokens.map(({ token, label }) => (
                    <Badge
                      key={token}
                      variant="secondary"
                      className="cursor-pointer text-xs"
                      onClick={() => insertToken(token)}
                      title={token}
                      data-testid={`badge-token-${label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {label}
                    </Badge>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </>
        )}
      </div>
      
      <div 
        className="p-3 cursor-text relative"
        style={{ minHeight }}
        onClick={() => editor.chain().focus().run()}
      >
        {editor.isEmpty && (
          <p className="text-muted-foreground text-sm pointer-events-none absolute top-3 left-3">
            {placeholder}
          </p>
        )}
        <EditorContent 
          editor={editor} 
          className="min-h-full"
        />
      </div>
    </div>
  );
});

export function getPlainTextFromHtml(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}
